import { useCallback, useEffect, useRef } from 'react';

import { Box, Button, Icon, IconButton, Image, Spinner, Stack, Text, useColorModeValue } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { createPortal } from 'react-dom';
import { BiArrowBack } from 'react-icons/bi';

import logo from 'assets/ecoQueryLogo.svg';
import logoWhite from 'assets/ecoQueryWhiteLogo.svg';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import ErrorState from './components/ErrorState';
import LoadingState from './components/LoadingState';
import ProgressBar from './components/ProgressBar';
import ExperienceStep from './steps/ExperienceStep';
import IndustryStep from './steps/IndustryStep';
import UseCasesStep from './steps/UseCasesStep';
import { QuestionnaireTrigger } from './types';
import useQuestionnaire from './useQuestionnaire';

interface QuestionnaireOverlayProps {
  isOpen: boolean;
  onClose?: () => void;
  onComplete: () => void;
  trigger: QuestionnaireTrigger;
  allowSkip: boolean;
}

const QuestionnaireOverlay = ({ isOpen, onClose, onComplete, trigger, allowSkip }: QuestionnaireOverlayProps) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const logoSrc = useColorModeValue(logo, logoWhite);
  const skipColor = useColorModeValue('gray.600', 'gray.400');
  const boxColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const {
    currentStep,
    answers,
    isSubmitting,
    isComplete,
    hasError,
    canProceed,
    setAnswer,
    nextStep,
    prevStep,
    submit,
    abandon,
    reset,
    setHasError,
    resetStartTime,
  } = useQuestionnaire(trigger);

  const hasTrackedStarted = useRef(false);

  useEffect(() => {
    if (isOpen && !hasTrackedStarted.current) {
      hasTrackedStarted.current = true;
      resetStartTime();
      mixpanel.track(MIXPANEL_EVENTS.QUESTIONNAIRE_STARTED, { trigger });
      mixpanel.track(MIXPANEL_EVENTS.QUESTIONNAIRE_STEP_VIEWED, { step: 1 });
    }
    if (!isOpen) {
      hasTrackedStarted.current = false;
    }
  }, [isOpen, trigger, resetStartTime]);

  const handleSkip = useCallback(() => {
    abandon();
    reset();
    onClose?.();
  }, [abandon, reset, onClose]);

  const handleComplete = () => {
    reset();
    onComplete();
  };

  const handleRetry = () => {
    setHasError(false);
    submit();
  };

  useEffect(() => {
    if (!isOpen || !allowSkip) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, allowSkip, handleSkip]);

  if (!isOpen) return null;

  const showSteps = !isSubmitting && !isComplete && !hasError;

  const content = (
    <Box position="fixed" inset={0} zIndex="modal" bg={bgColor} display="flex" flexDirection="column">
      <Box position="relative" px={4} py={3}>
        <Stack align="center" spacing={2}>
          <Image src={logoSrc} alt="ecoQuery" h="28px" />
          {showSteps && <ProgressBar currentStep={currentStep} totalSteps={3} />}
        </Stack>

        <Box position="absolute" left={4} top={3}>
          {currentStep > 1 && showSteps && (
            <IconButton
              aria-label="Go back"
              icon={<Icon as={BiArrowBack} boxSize={5} />}
              variant="ghost"
              colorScheme="green"
              onClick={prevStep}
              size="sm"
            />
          )}
          {hasError && (
            <IconButton
              aria-label="Go back"
              icon={<Icon as={BiArrowBack} boxSize={5} />}
              variant="ghost"
              colorScheme="green"
              onClick={() => setHasError(false)}
              size="sm"
            />
          )}
        </Box>

        {allowSkip && showSteps && (
          <Box position="absolute" right={4} top={3}>
            <Text
              as="button"
              fontSize="sm"
              color={skipColor}
              onClick={handleSkip}
              cursor="pointer"
              whiteSpace="nowrap"
              lineHeight="40px"
              _hover={{ textDecoration: 'underline' }}
            >
              Remind me later
            </Text>
          </Box>
        )}
      </Box>

      <Box borderBottomWidth="1px" borderColor={boxColor} />

      <Stack
        flex={1}
        align="center"
        justify="center"
        px={{ base: 4, md: 8 }}
        py={8}
        overflowY="auto"
        maxW="600px"
        w="100%"
        mx="auto"
      >
        {showSteps && currentStep === 1 && (
          <ExperienceStep value={answers.experienceLevel} onChange={(v) => setAnswer('experienceLevel', v)} />
        )}
        {showSteps && currentStep === 2 && (
          <UseCasesStep
            value={answers.useCases}
            otherText={answers.useCasesOther}
            onChange={(v) => setAnswer('useCases', v)}
            onOtherTextChange={(v) => setAnswer('useCasesOther', v)}
          />
        )}
        {showSteps && currentStep === 3 && <IndustryStep value={answers.industry} onChange={(v) => setAnswer('industry', v)} />}

        {isSubmitting && (
          <Stack align="center" justify="center" flex={1} spacing={4}>
            <Spinner size="xl" color="green.500" thickness="3px" />
            <Text color={textColor}>Setting up your experience…</Text>
          </Stack>
        )}

        {isComplete && !hasError && <LoadingState onFinished={handleComplete} />}

        {hasError && <ErrorState onRetry={handleRetry} onSkip={handleSkip} allowSkip={allowSkip} />}
      </Stack>

      {showSteps && (
        <Stack px={{ base: 4, md: 8 }} pb={8} align="center" spacing={3}>
          <Button
            colorScheme="green"
            size="lg"
            w="100%"
            maxW="400px"
            isDisabled={!canProceed}
            onClick={currentStep === 3 ? submit : nextStep}
          >
            {currentStep === 3 ? (trigger === 'first_login' ? 'Complete & Start' : 'Finish') : 'Next'}
          </Button>
          {currentStep === 3 && (
            <Text fontSize="xs" color={textColor} textAlign="center" maxW="400px">
              This information is used only to tailor ecoQuery to your needs. It is processed in accordance with our{' '}
              <Text
                as="a"
                href="https://ecoinvent.org/gdpr-eula#privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                textDecoration="underline"
              >
                Privacy Policy
              </Text>
              .
            </Text>
          )}
        </Stack>
      )}
    </Box>
  );

  return createPortal(content, document.body);
};

export default QuestionnaireOverlay;
